import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useCategories, catChipSolid } from "@/context/CategoriesContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Tags, Plus, Trash2, Loader2 } from "lucide-react";

const SWATCHES = ["#fb7185", "#f472b6", "#e879f9", "#a78bfa", "#818cf8", "#60a5fa", "#38bdf8", "#2dd4bf", "#34d399", "#a3e635", "#fbbf24", "#fb923c", "#f87171", "#94a3b8"];

function TextToggle({ value, onChange }) {
  const opts = [["white", "#ffffff"], ["black", "#0f172a"]];
  return (
    <div className="flex gap-1 shrink-0">
      {opts.map(([v, bg]) => (
        <button key={v} type="button" onClick={() => onChange(v)} data-testid={`cat-text-${v}`}
          title={v === "white" ? "Testo bianco" : "Testo nero"}
          className={`w-6 h-6 rounded border flex items-center justify-center text-xs font-bold transition-all ${value === v ? "border-cyan-400 ring-1 ring-cyan-400" : "border-slate-600"}`}
          style={{ backgroundColor: bg, color: v === "white" ? "#0f172a" : "#ffffff" }}>A</button>
      ))}
    </div>
  );
}

export function CategoriesEditor() {
  const { categories, refresh } = useCategories();
  const [name, setName] = useState("");
  const [color, setColor] = useState("#38bdf8");
  const [textColor, setTextColor] = useState("white");
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!name.trim()) { toast.error("Inserisci un nome"); return; }
    setBusy(true);
    try {
      await api.post("/categories", { name, color, text_color: textColor });
      toast.success("Categoria aggiunta");
      setName(""); setColor("#38bdf8"); setTextColor("white");
      await refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const changeColor = async (cat, c) => {
    try { await api.put(`/categories/${cat.id}`, { color: c }); await refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const changeTextColor = async (cat, tc) => {
    try { await api.put(`/categories/${cat.id}`, { text_color: tc }); await refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (cat) => {
    if (!window.confirm(`Eliminare la categoria "${cat.name}"? Gli eventi collegati resteranno senza categoria.`)) return;
    try { await api.delete(`/categories/${cat.id}`); toast.success("Categoria eliminata"); await refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <>
      <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
        {categories.map((c) => (
          <div key={c.id} data-testid={`cat-row-${c.name}`} className="flex items-center gap-2 rounded-lg border border-slate-800 bg-slate-800/40 p-2.5">
            <input type="color" aria-label="colore" value={c.color || "#38bdf8"} onChange={(e) => changeColor(c, e.target.value)}
              data-testid={`cat-color-${c.name}`} className="w-8 h-8 rounded cursor-pointer bg-transparent border border-slate-700 p-0.5 shrink-0" />
            <span className="inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-bold truncate"
              style={catChipSolid(c.color, c.text_color)}>{c.name}</span>
            <span className="flex-1" />
            <TextToggle value={c.text_color || "white"} onChange={(v) => changeTextColor(c, v)} />
            <Button variant="ghost" size="icon" data-testid={`cat-delete-${c.name}`} onClick={() => remove(c)} className="text-rose-300 hover:bg-rose-500/10 h-8 w-8 shrink-0">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}
        {categories.length === 0 && <p className="text-sm text-slate-500">Nessuna categoria.</p>}
      </div>

      <div className="border-t border-slate-800 pt-3 space-y-2">
        <p className="text-sm font-medium">Nuova categoria</p>
        <div className="flex items-center gap-2">
          <input type="color" value={color} onChange={(e) => setColor(e.target.value)} data-testid="new-cat-color"
            className="w-9 h-9 rounded cursor-pointer bg-transparent border border-slate-700 p-0.5 shrink-0" />
          <Input data-testid="new-cat-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Es. MATRIMONI"
            className="bg-slate-800 border-slate-700 uppercase" />
          <Button onClick={add} disabled={busy} data-testid="add-cat-btn" className="bg-cyan-500 hover:bg-cyan-600 text-slate-950 font-semibold shrink-0">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          </Button>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex flex-wrap gap-1.5">
            {SWATCHES.map((s) => (
              <button key={s} type="button" onClick={() => setColor(s)} aria-label={s}
                className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ${color === s ? "border-white" : "border-transparent"}`}
                style={{ backgroundColor: s }} />
            ))}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400">Testo:</span>
            <TextToggle value={textColor} onChange={setTextColor} />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">Anteprima:</span>
          <span className="inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-bold" style={catChipSolid(color, textColor)}>{name.trim().toUpperCase() || "CATEGORIA"}</span>
        </div>
      </div>
    </>
  );
}

export function CategoriesPanel() {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900 p-4 sm:p-6 max-w-2xl" data-testid="categories-panel">
      <div className="flex items-center gap-2 mb-1">
        <Tags className="w-5 h-5 text-cyan-400" />
        <h2 className="font-display text-xl font-semibold">Categorie eventi</h2>
      </div>
      <p className="text-sm text-slate-400 mb-4">Aggiungi, cambia colore o elimina le categorie. I colori appaiono su eventi e calendario.</p>
      <CategoriesEditor />
    </div>
  );
}

export function CategoryManagerDialog() {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="manage-categories-btn" className="border-slate-700 text-slate-200 hover:bg-slate-800">
          <Tags className="w-4 h-4 mr-1.5" /> Categorie
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">Gestisci categorie</DialogTitle>
          <DialogDescription className="text-slate-400 text-sm">Aggiungi, cambia colore o elimina le categorie degli eventi.</DialogDescription>
        </DialogHeader>
        <CategoriesEditor />
      </DialogContent>
    </Dialog>
  );
}
